import { SPEC_PROPERTIES, } from './specs';
/**
 * One spec value as one line of text, the way the CSV writes it and the admin panel shows it:
 * `1080x?`, `1.91:1..4:5, 9:16`, `mp4, mov`, `23.976..60`, `250, 0, 340, 0`, or `N/A`. File sizes
 * are written in KB; every other number is in the property's base unit. Keeping one codec means a
 * value typed in the panel and one imported from a spreadsheet can never be read two ways.
 *
 * Lists are read on `,` or `|`, and a safe zone may end in `px`: both are how files written by
 * hand or by an earlier export spell them, and neither is ambiguous.
 */
export const SPEC_CELL_NOT_APPLICABLE = 'N/A';
const LIST_WRITE_SEPARATOR = ', ';
const LIST_READ_SEPARATOR = /[,|]/;
const RANGE_SEPARATOR = '..';
/** Matches the byte values already in the library, where 30 MB is 31457280. */
export const BYTES_PER_KB = 1024;
const FILE_SIZE_PROPERTIES = new Set([
    'fileSize.max',
    'thumbnail.fileSize.max',
]);
/** A property stored in bytes but written, read and edited in KB. */
export const isFileSizeProperty = (property) => FILE_SIZE_PROPERTIES.has(property);
export const bytesToKb = (bytes) => Number((bytes / BYTES_PER_KB).toFixed(2));
/** The kind of value a property holds, or undefined for a property the registry does not list. */
export function specValueKind(mediaType, property) {
    if (!(mediaType in SPEC_PROPERTIES))
        return undefined;
    const registry = SPEC_PROPERTIES[mediaType];
    return registry[property];
}
const formatRange = (from, to) => `${from}${RANGE_SEPARATOR}${to}`;
const splitList = (cell) => cell
    .split(LIST_READ_SEPARATOR)
    .map((part) => part.trim())
    .filter(Boolean);
export function encodeSpecCell(kind, value, property = '') {
    if (value === null)
        return SPEC_CELL_NOT_APPLICABLE;
    switch (kind) {
        case 'size': {
            const size = value;
            return `${size.width ?? '?'}x${size.height ?? '?'}`;
        }
        case 'ratioList':
            return value
                .map((entry) => (typeof entry === 'string' ? entry : formatRange(entry.from, entry.to)))
                .join(LIST_WRITE_SEPARATOR);
        case 'stringList':
            return value.join(LIST_WRITE_SEPARATOR);
        case 'numberSet':
            return Array.isArray(value)
                ? value.join(LIST_WRITE_SEPARATOR)
                : formatRange(value.min, value.max);
        case 'margins': {
            const { top, right, bottom, left } = value;
            return [top, right, bottom, left].join(LIST_WRITE_SEPARATOR);
        }
        case 'number':
            return String(isFileSizeProperty(property) ? bytesToKb(value) : value);
        case 'boolean':
            return String(value);
        case 'ratio':
        case 'text':
        case 'transparency':
            return value;
    }
}
function toNumber(cell) {
    if (!/^\d+(\.\d+)?$/.test(cell))
        return null;
    return Number(cell);
}
/** The text back into a value. Its shape and provenance are checked again when it is saved. */
export function decodeSpecCell(kind, rawCell, property = '') {
    const cell = rawCell.trim();
    if (cell === SPEC_CELL_NOT_APPLICABLE)
        return { value: null };
    switch (kind) {
        case 'size': {
            const match = /^(\d+|\?)\s*x\s*(\d+|\?)$/i.exec(cell);
            if (!match)
                return { error: 'size_format' };
            const side = (part) => part === '?' || part === undefined ? null : Number(part);
            return { value: { width: side(match[1]), height: side(match[2]) } };
        }
        case 'ratioList':
            return {
                value: splitList(cell).map((entry) => {
                    const parts = entry.split(RANGE_SEPARATOR).map((part) => part.trim());
                    return parts.length === 2 ? { from: parts[0], to: parts[1] } : entry;
                }),
            };
        case 'stringList':
            return { value: splitList(cell) };
        case 'number': {
            const number = toNumber(cell);
            if (!isFileSizeProperty(property))
                return number === null ? { error: 'number_format' } : { value: number };
            return number === null
                ? { error: 'kb_format' }
                : { value: Math.round(number * BYTES_PER_KB) };
        }
        case 'numberSet': {
            if (cell.includes(RANGE_SEPARATOR)) {
                const bounds = cell.split(RANGE_SEPARATOR).map((part) => toNumber(part.trim()));
                const [min, max] = bounds;
                return bounds.length !== 2 || min === null || max === null
                    ? { error: 'range_format' }
                    : { value: { min, max } };
            }
            const numbers = splitList(cell).map(toNumber);
            return numbers.length === 0 || numbers.some((n) => n === null)
                ? { error: 'number_list_format' }
                : { value: numbers };
        }
        case 'boolean':
            return cell === 'true' || cell === 'false'
                ? { value: cell === 'true' }
                : { error: 'boolean_format' };
        case 'margins': {
            const sides = cell
                .replace(/px$/i, '')
                .split(',')
                .map((part) => toNumber(part.trim()));
            if (sides.length !== 4 || sides.some((side) => side === null))
                return { error: 'margins_format' };
            const [top, right, bottom, left] = sides;
            return { value: { top, right, bottom, left, unit: 'px' } };
        }
        case 'ratio':
        case 'text':
        case 'transparency':
            return { value: cell };
    }
}
